import React, { useEffect, useRef, useState } from 'react';
import { 
  Brain, 
  ChevronRight, 
  Layers, 
  LineChart, 
  MessageSquare, 
  Shield, 
  Users, 
  Zap,
  ArrowRight,
  Globe,
  Award,
  BookOpen,
  Sparkles,
  MousePointer2,
  X,
  CheckCircle2,
  Loader2
} from 'lucide-react';

interface LandingPageProps {
  onLoginClick: () => void;
}

const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: { x: number; y: number; dx: number; dy: number; size: number }[] = [];

    const init = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      particles = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 15000);
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          dx: (Math.random() - 0.5) * 0.5,
          dy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2,
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(99, 102, 241, 0.2)';
      
      particles.forEach((p, i) => {
        p.x += p.dx;
        p.y += p.dy;

        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Connect particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (dist < 150) {
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.15 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    const handleResize = () => init();
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none opacity-40" />;
};

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [activeReportPage, setActiveReportPage] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactType, setRequestType] = useState<'demo' | 'soporte' | 'evolucion'>('demo');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    institution: '',
    message: ''
  });
  const revealRefs = useRef<(HTMLElement | null)[]>([]);

  const handleCTA = (type: 'demo' | 'soporte' | 'evolucion') => {
    setRequestType(type);
    setShowContactModal(true);
    setFormStatus('idle');
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');

    try {
      // 🔍 Detectar URL de la API: localhost:5000 en dev, relativo en prod
      const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
      console.log(`📡 Enviando solicitud a: ${API_URL}/api/public/contact`);
      
      const response = await fetch(`${API_URL}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, type: contactType })
      });

      // Si no es JSON, capturar el error antes de intentar parsear
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("❌ Respuesta no es JSON:", text.substring(0, 100));
        throw new Error(`Servidor retornó ${response.status}: No JSON`);
      }

      const result = await response.json();
      if (result.success) {
        console.log("✅ Solicitud enviada con éxito");
        setFormStatus('success');
        setFormData({ name: '', email: '', institution: '', message: '' });
        setTimeout(() => setShowContactModal(false), 3000);
      } else {
        console.error("❌ Error del servidor:", result.message);
        setFormStatus('error');
      }
    } catch (error) {
      console.error('❌ Error submitting contact form:', error);
      setFormStatus('error');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      revealRefs.current.forEach((ref) => {
        if (ref) {
          const { top } = ref.getBoundingClientRect();
          if (top < window.innerHeight * 0.9) {
            ref.classList.add('active');
          }
        }
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const addToRevealRefs = (el: HTMLElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 overflow-x-hidden selection:bg-indigo-500/30 selection:text-white">
      {/* Mesh Background Decorations */}
      <div className="mesh-bg">
        <div className="mesh-circle w-[600px] h-[600px] bg-indigo-600 top-[-200px] left-[-100px]" />
        <div className="mesh-circle w-[500px] h-[500px] bg-purple-600 bottom-[-100px] right-[-100px]" style={{ animationDelay: '-5s' }} />
      </div>

      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          scrolled ? 'bg-slate-900/95 backdrop-blur-2xl py-3 shadow-2xl border-b border-white/5' : 'bg-transparent py-8'
        }`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          {/* Logo - Left */}
          <div className="flex-1 flex justify-start">
            <div className="flex items-center group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="h-16 flex items-center justify-center transition-all duration-500 group-hover:scale-105">
                <img 
                  src={`${process.env.PUBLIC_URL}/images/logos/logo-csn.png`} 
                  alt="Logo Colegio Sin Notas" 
                  className="h-full w-auto object-contain brightness-110" 
                />
              </div>
            </div>
          </div>

          {/* Nav - Center */}
          <nav className="hidden lg:flex items-center gap-10 text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            <a href="#metodologia" className="hover:text-indigo-400 transition-colors">Metodología</a>
            <a href="#proposito" className="hover:text-indigo-400 transition-colors">Propósito</a>
            <a href="#colegios" className="hover:text-indigo-400 transition-colors">Impacto</a>
            <a href="#dimensiones" className="hover:text-indigo-400 transition-colors">Estructura</a>
          </nav>

          {/* Button - Right */}
          <div className="flex-1 flex justify-end">
            <button 
              onClick={onLoginClick}
              className="hidden md:block px-8 py-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-500 transition-all duration-300 shadow-lg shadow-indigo-900/40 hover:scale-105 active:scale-95 border border-indigo-400/20 font-black text-xs uppercase tracking-widest"
            >
              Iniciar sesión
            </button>
            <button className="md:hidden text-white p-3 glass rounded-2xl">
              <div className="w-6 h-0.5 bg-white mb-1.5"></div>
              <div className="w-6 h-0.5 bg-white mb-1.5"></div>
              <div className="w-4 h-0.5 bg-white"></div>
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden landing-grid">
          <ParticleBackground />
          <div className="container mx-auto px-6 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="w-full lg:w-3/5">
                <div className="reveal active mb-8" ref={addToRevealRefs}>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-indigo-400 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur-md">
                    <Sparkles size={14} /> La Evolución Pedagógica 2025
                  </div>
                </div>
                
                <h1 className="reveal active text-6xl md:text-8xl font-extrabold leading-[1] mb-10 tracking-tighter" ref={addToRevealRefs}>
                  Más allá de las <span className="font-editorial italic font-normal text-indigo-400 opacity-90">notas</span>,<br />
                  iluminamos el <span className="gradient-text">potencial</span>.
                </h1>
                
                <div className="flex flex-col sm:flex-row gap-8 items-start reveal active" ref={addToRevealRefs}>
                  <p className="text-xl text-slate-400 max-w-xl leading-relaxed border-l-2 border-indigo-500/30 pl-8">
                    El primer sistema de inteligencia educativa enfocado en la trazabilidad 
                    humana y el desarrollo de habilidades neuro-pedagógicas.
                  </p>
                  <div className="flex flex-col gap-4 w-full sm:w-auto">
                    <button 
                      onClick={() => handleCTA('evolucion')}
                      className="group px-10 py-5 bg-indigo-600 text-white rounded-full font-bold flex items-center justify-center gap-3 hover:bg-indigo-500 transition-all duration-500 shadow-2xl shadow-indigo-500/30 hover:scale-105"
                    >
                      Empezar Evolución <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <div className="flex items-center gap-2 text-xs text-slate-500 font-bold justify-center sm:justify-start">
                      <MousePointer2 size={12} /> INTERACCIÓN DINÁMICA ACTIVA
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive Visual Element */}
              <div className="w-full lg:w-2/5 hidden lg:block reveal active" ref={addToRevealRefs}>
                <div 
                  className="relative w-80 h-80 mx-auto"
                  style={{
                    transform: `translate(${(mousePos.x - window.innerWidth/2) / 50}px, ${(mousePos.y - window.innerHeight/2) / 50}px)`
                  }}
                >
                  <div className="absolute inset-0 bg-indigo-600/20 rounded-full blur-[100px] animate-pulse"></div>
                  <div className="absolute inset-0 glass rounded-[4rem] rotate-12 flex items-center justify-center overflow-hidden group hover:rotate-6 transition-all duration-700">
                    <div className="w-48 h-48 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-2xl shadow-indigo-500/50">
                      <Zap size={80} className="text-white float" />
                    </div>
                    {/* Floating mini cards */}
                    <div className="absolute top-8 left-8 w-12 h-12 glass rounded-xl flex items-center justify-center text-indigo-400">
                      <Brain size={24} />
                    </div>
                    <div className="absolute bottom-12 right-6 w-16 h-16 glass rounded-2xl flex items-center justify-center text-purple-400">
                      <LineChart size={32} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-12 reveal-stagger" ref={addToRevealRefs}>
              {[
                { label: 'NORMATIVA', val: '100%', sub: 'Objetivos de Aprendizaje MINEDUC' },
                { label: 'TRAZABILIDAD', val: '1000+', sub: 'Habilidades Mapeadas' },
                { label: 'ESTRUCTURA', val: '3 Dim.', sub: '11 Indicadores Clave' },
                { label: 'SISTEMA', val: 'SIGE', sub: 'Integración Oficial' }
              ].map((s, i) => (
                <div key={i} className="group text-center md:text-left relative">
                  <div className="absolute -left-4 top-0 w-1 h-12 bg-indigo-500/0 group-hover:bg-indigo-500 transition-all duration-500 rounded-full" />
                  <div className="text-5xl font-black mb-2 tracking-tight text-white">{s.val}</div>
                  <div className="text-xs text-indigo-400 uppercase tracking-[0.3em] font-bold mb-1">{s.label}</div>
                  <div className="text-sm text-slate-500">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section id="metodologia" className="py-32 relative">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row gap-20 items-center">
              <div className="w-full lg:w-1/2 reveal" ref={addToRevealRefs}>
                <h2 className="text-5xl md:text-6xl font-extrabold mb-10 leading-tight tracking-tighter">
                  Nuestra <span className="font-editorial italic font-normal text-indigo-400">Travesía</span> pedagógica.
                </h2>
                <div className="grid gap-6">
                  {[
                    { icon: <Zap />, title: 'Alineación Curricular', desc: 'Nuestra travesía pedagógica integra los Objetivos de Aprendizaje del MINEDUC, garantizando rigor normativo y excelencia educativa.' },
                    { icon: <LineChart />, title: 'Trazabilidad Masiva', desc: 'Seguimos la evolución en más de 1000 habilidades a lo largo de los años, identificando brechas claras para trabajarlas con la comunidad.' },
                    { icon: <Shield />, title: 'Objetividad Pedagógica', desc: 'Registro permanente que evita juicios subjetivos, enfocándose en la evidencia real del progreso estudiantil.' }
                  ].map((m, i) => (
                    <div key={i} className="glass-card p-8 rounded-3xl flex gap-6 group">
                      <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-500 group-hover:text-white transition-all duration-500">
                        {m.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2 text-white">{m.title}</h3>
                        <p className="text-slate-400 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2 reveal" ref={addToRevealRefs}>
                <div className="relative aspect-square">
                  <div className="absolute inset-0 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 rounded-[4rem] blur-3xl"></div>
                  <div className="relative h-full w-full glass rounded-[4rem] flex flex-col items-center justify-center p-12 text-center overflow-hidden border-indigo-500/20 border-2">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                    <Sparkles size={80} className="text-indigo-500 mb-10 float" />
                    <h4 className="text-3xl font-bold font-editorial italic text-white mb-6">"Entendemos el desarrollo como un proceso, honrando la individualidad."</h4>
                    <p className="text-slate-400 max-w-sm">Cada niño es un mundo. Nuestra tecnología permite que cada uno brille en su propio tiempo y forma.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem vs Solution Section */}
        <section id="proposito" className="py-32 bg-slate-900/50 relative">
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-2 gap-32 items-center">
              <div className="reveal" ref={addToRevealRefs}>
                <div className="mb-8 flex items-center gap-4">
                  <div className="h-[2px] w-12 bg-indigo-500"></div>
                  <span className="text-xs font-bold tracking-[0.4em] uppercase text-indigo-400">El Diferenciador</span>
                </div>
                <h2 className="text-5xl md:text-7xl font-extrabold mb-12 leading-[1] tracking-tighter text-white">
                  Identificar no es <span className="text-rose-500 font-editorial italic">calificar</span>.
                </h2>
                
                <div className="space-y-16">
                  <div className="relative group">
                    <div className="absolute -left-12 top-0 text-rose-500 font-bold opacity-20 text-4xl group-hover:opacity-100 transition-opacity">01</div>
                    <h3 className="text-2xl font-bold text-rose-500 mb-4 tracking-tight">Limitación Tradicional</h3>
                    <p className="text-slate-400 text-lg leading-relaxed border-l border-rose-500/20 pl-8">
                      La nota estándar simplifica el proceso humano. Oculta las fortalezas 
                      específicas y no ofrece una hoja de ruta clara para la mejora.
                    </p>
                  </div>
                  
                  <div className="relative group">
                    <div className="absolute -left-12 top-0 text-indigo-500 font-bold opacity-20 text-4xl group-hover:opacity-100 transition-opacity">02</div>
                    <h3 className="text-2xl font-bold text-indigo-400 mb-4 tracking-tight">Evolución Sin Notas</h3>
                    <p className="text-slate-200 text-lg leading-relaxed border-l border-indigo-500/40 pl-8">
                      Medimos habilidades granulares. Sabemos exactamente dónde está el 
                      estudiante hoy y cómo impulsarlo hacia su próximo hito.
                    </p>
                  </div>
                </div>
              </div>

              <div className="reveal relative" ref={addToRevealRefs}>
                <div className="glass-card rounded-[3.5rem] p-4 relative overflow-hidden group border-2 border-indigo-500/20">
                  {/* Carousel of Report Images */}
                  <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[2.5rem] bg-slate-950">
                    <div 
                      className="flex transition-transform duration-700 ease-in-out h-full"
                      style={{ transform: `translateX(-${activeReportPage * 100}%)` }}
                    >
                      {[
                        { 
                          img: `${process.env.PUBLIC_URL}/images/web/informe-portada.png`, 
                          title: 'Portada del Informe', 
                          desc: 'Identificación oficial del estudiante y equipo docente del Ciclo Scott.' 
                        },
                        { 
                          img: `${process.env.PUBLIC_URL}/images/web/informe-graficos.png`, 
                          title: 'Gráficos de Desarrollo', 
                          desc: 'Visualización clara de niveles logrados vs etapa esperada por edad.' 
                        },
                        { 
                          img: `${process.env.PUBLIC_URL}/images/web/informe-cualitativo.png`, 
                          title: 'Análisis Cualitativo', 
                          desc: 'Descripción pedagógica profunda y personalizada de cada estudiante.' 
                        },
                        { 
                          img: `${process.env.PUBLIC_URL}/images/web/formularios.png`, 
                          title: 'Gestión de Habilidades', 
                          desc: 'Interfaz intuitiva para el registro permanente de evidencias y progresos.' 
                        },
                        { 
                          img: `${process.env.PUBLIC_URL}/images/web/formularios-2.png`, 
                          title: 'Registro de Evidencias', 
                          desc: 'Captura en tiempo real del progreso individual mediante indicadores claros y precisos.' 
                        }
                      ].map((p, i) => (
                        <div key={i} className="min-w-full h-full relative group/item">
                          <img 
                            src={p.img} 
                            alt={p.title} 
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent">
                            <h4 className="text-xl font-bold text-white mb-1">{p.title}</h4>
                            <p className="text-sm text-slate-400">{p.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Carousel Controls */}
                  <div className="absolute top-1/2 left-4 right-4 -translate-y-1/2 flex justify-between pointer-events-none">
                    <button 
                      onClick={() => setActiveReportPage(prev => (prev > 0 ? prev - 1 : 4))}
                      className="w-12 h-12 glass rounded-full flex items-center justify-center text-white pointer-events-auto hover:bg-indigo-600 transition-colors"
                    >
                      <ChevronRight className="rotate-180" size={24} />
                    </button>
                    <button 
                      onClick={() => setActiveReportPage(prev => (prev < 4 ? prev + 1 : 0))}
                      className="w-12 h-12 glass rounded-full flex items-center justify-center text-white pointer-events-auto hover:bg-indigo-600 transition-colors"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>

                  {/* Indicators */}
                  <div className="flex justify-center gap-3 mt-6">
                    {[0, 1, 2, 3, 4].map(i => (
                      <button 
                        key={i}
                        onClick={() => setActiveReportPage(i)}
                        className={`h-2 rounded-full transition-all duration-500 ${
                          activeReportPage === i ? 'w-8 bg-indigo-500' : 'w-2 bg-white/20'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Annotation Tooltip (Simulating Hotspots) */}
                <div className="absolute -bottom-10 -right-10 glass p-6 rounded-2xl max-w-xs shadow-2xl border border-indigo-500/30 hidden md:block animate-bounce-slow">
                  <div className="flex items-center gap-3 mb-2 text-indigo-400">
                    <Sparkles size={18} />
                    <span className="font-bold text-xs uppercase tracking-widest">Interpretación del Camino</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Las <span className="text-indigo-400 font-bold">barras azules</span> son el nivel actual, los <span className="text-amber-400 font-bold">bloques amarillos</span> la etapa esperada por edad, y la <span className="text-white font-bold">línea vertical</span> el progreso previo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section id="colegios" className="py-32 relative overflow-hidden">
          <div className="container mx-auto px-6">
            <div className="text-center mb-24 reveal" ref={addToRevealRefs}>
              <h2 className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tighter text-white">Impacto <span className="gradient-text">Real</span>.</h2>
              <p className="text-slate-400 text-xl max-w-3xl mx-auto font-medium">
                Transformando la educación chilena, desde la vanguardia privada 
                hasta la excelencia en el servicio público.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 reveal-stagger" ref={addToRevealRefs}>
              <div className="glass-card p-12 rounded-[3rem] group">
                <div className="w-20 h-20 bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  <Award className="text-indigo-500" size={40} />
                </div>
                <h3 className="text-3xl font-bold mb-6 text-white tracking-tight">Colegio Puerto Varas</h3>
                <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                  Referente regional en innovación. Implementación de trazabilidad 
                  de habilidades desde pre-básica, elevando el estándar de comunicación familiar.
                </p>
                <div className="flex items-center gap-3 text-indigo-400 font-black text-sm uppercase tracking-[0.2em]">
                  EXCELENCIA ACADÉMICA <ChevronRight size={18} />
                </div>
              </div>

              <div className="glass-card p-12 rounded-[3rem] group">
                <div className="w-20 h-20 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-10 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500">
                  <Globe className="text-purple-500" size={40} />
                </div>
                <h3 className="text-3xl font-bold mb-6 text-white tracking-tight">Mirador del Lago</h3>
                <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                  Liderando la transformación digital en el SLEP Llanquihue, demostrando 
                  que los datos con sentido humano revolucionan la educación pública.
                </p>
                <div className="flex items-center gap-3 text-purple-400 font-black text-sm uppercase tracking-[0.2em]">
                  IMPACTO SOCIAL <ChevronRight size={18} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dimensions Section */}
        <section id="dimensiones" className="py-32 relative bg-indigo-950/20">
          <div className="container mx-auto px-6">
            <div className="flex flex-col lg:flex-row justify-between items-end mb-24 reveal" ref={addToRevealRefs}>
              <div className="max-w-2xl">
                <div className="h-1 w-20 bg-gradient-to-r from-indigo-500 to-purple-500 mb-8" />
                <h2 className="text-5xl md:text-6xl font-extrabold mb-8 tracking-tighter text-white">Estructura del Ser.</h2>
                <p className="text-slate-400 text-xl">
                  Un sistema basado en los Objetivos de Aprendizaje del MINEDUC, organizado en 3 dimensiones fundamentales y 11 indicadores que contienen más de 1000 habilidades trazables.
                </p>
              </div>
              <button 
                onClick={onLoginClick}
                className="mt-8 lg:mt-0 flex items-center gap-3 font-black text-indigo-400 hover:text-white transition-all uppercase tracking-widest text-sm"
              >
                Explorar Todas las Habilidades <ArrowRight size={20} />
              </button>
            </div>

            <div className="grid gap-16">
              {[
                { 
                  category: "Exploración de uno mismo",
                  desc: "Dimensiones enfocadas en el desarrollo interno, la conciencia personal y la expresión propia, acorde a nuestro Proyecto Educativo Institucional.",
                  dimensions: [
                    { n: 'Autoconciencia y Voluntad', i: <Brain /> },
                    { n: 'Habilidades de Pensamiento', i: <Zap /> },
                    { n: 'Habilidades comunicacionales y del lenguaje español', i: <MessageSquare /> },
                    { n: 'Corporalidad y Movimiento', i: <Sparkles /> },
                    { n: 'Lenguajes Artísticos', i: <Layers /> },
                    { n: 'Motivación y disposición frente al aprendizaje', i: <Award /> },
                  ]
                },
                { 
                  category: "Exploración del mundo",
                  desc: "Relación del estudiante con su entorno natural, social y el desarrollo del pensamiento lógico-matemático.",
                  dimensions: [
                    { n: 'Encuentro con la naturaleza y la sociedad', i: <Globe /> },
                    { n: 'Habilidades de relación y conciencia social', i: <Users /> },
                    { n: 'Pensamiento Matemático', i: <LineChart /> },
                    { n: 'Acercamiento a la lengua inglesa', i: <BookOpen /> },
                  ]
                },
                { 
                  category: "Exploración de lo Trascendente",
                  desc: "Desarrollo del sentido espiritual, valores cristianos y el vínculo con lo divino en el Ciclo Scott.",
                  dimensions: [
                    { n: 'Amor a Dios y sentido Cristiano', i: <Sparkles className="text-amber-400" /> },
                  ]
                }
              ].map((cat, ci) => (
                <div key={ci} className="reveal" ref={addToRevealRefs}>
                  <div className="flex items-center gap-6 mb-10">
                    <div className="h-[1px] flex-grow bg-white/10"></div>
                    <h3 className="text-2xl font-black text-indigo-400 uppercase tracking-[0.3em] whitespace-nowrap">{cat.category}</h3>
                    <div className="h-[1px] flex-grow bg-white/10"></div>
                  </div>
                  <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto italic">{cat.desc}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                    {cat.dimensions.map((f, i) => (
                      <div key={i} className={`glass-card p-6 rounded-3xl group hover:bg-indigo-500/10 text-center flex flex-col items-center justify-center min-h-[180px] ${cat.category === 'Exploración de lo Trascendente' ? 'border-amber-500/20' : ''}`}>
                        <div className={`w-12 h-12 bg-white/5 ${cat.category === 'Exploración de lo Trascendente' ? 'text-amber-400' : 'text-indigo-400'} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-indigo-500 group-hover:text-white transition-all duration-500 shadow-xl shadow-black/20`}>
                          {f.i}
                        </div>
                        <h4 className="font-extrabold text-xs text-white leading-tight uppercase tracking-wider">{f.n}</h4>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Qualitative / Collaborative Work Section */}
        <section className="py-32 relative bg-slate-900/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-24 reveal" ref={addToRevealRefs}>
              <h2 className="text-5xl md:text-7xl font-extrabold mb-8 tracking-tighter text-white">
                El Valor de lo <span className="font-editorial italic font-normal text-indigo-400">Cualitativo</span>.
              </h2>
              <p className="text-slate-400 text-xl max-w-3xl mx-auto font-medium">
                Más que datos, construimos puentes de entendimiento entre el aula y el hogar 
                mediante una narrativa pedagógica rigurosa y empática.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 reveal-stagger" ref={addToRevealRefs}>
              {[
                { 
                  name: 'Matilde', 
                  excerpt: '...demuestra ser una niña cariñosa y respetuosa. En el trabajo de clases se muestra motivada y alerta. Su desafío es atreverse a hablar en público y dar su opinión frente a otros...',
                  focus: 'Desarrollo Social y Matemáticas'
                },
                { 
                  name: 'Clemente', 
                  excerpt: '...grandes logros en el seguimiento de instrucciones y trabajo colaborativo. En matemáticas, se desafía a reforzar el valor posicional mediante ejercicios prácticos en el hogar...',
                  focus: 'Autonomía e Idioma Español'
                },
                { 
                  name: 'Dominga', 
                  excerpt: '...actitud favorable para enfrentar nuevos desafíos. Destaca por su perseverancia. Se sugiere potenciar la fluidez lectora mediante manipulación de letras en casa...',
                  focus: 'Esfuerzo y Lectoescritura'
                }
              ].map((ex, i) => (
                <div key={i} className="glass-card p-10 rounded-[2.5rem] relative group border-t-4 border-indigo-500/30">
                  <div className="absolute -top-6 left-10 px-4 py-2 bg-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest text-white">
                    CASO DE ESTUDIO
                  </div>
                  <MessageSquare className="text-indigo-500 mb-8 opacity-40" size={32} />
                  <h4 className="text-2xl font-bold text-white mb-2 tracking-tight">{ex.name}</h4>
                  <p className="text-xs text-indigo-400 font-bold mb-6 uppercase tracking-widest">{ex.focus}</p>
                  <p className="text-slate-400 italic leading-relaxed font-editorial text-lg mb-8">
                    "{ex.excerpt}"
                  </p>
                  <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Acompañamiento 360°</span>
                    <button className="text-indigo-400 hover:text-white transition-colors">
                      <ArrowRight size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-20 glass-card p-12 rounded-[3.5rem] reveal" ref={addToRevealRefs}>
              <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="w-full md:w-1/3">
                  <h3 className="text-3xl font-bold text-white mb-6">Invitación al Trabajo Colaborativo</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Nuestras descripciones evitan juicios subjetivos, enfocándose en 
                    estrategias concretas para que los padres acompañen el proceso 
                    desde casa de manera efectiva.
                  </p>
                </div>
                <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="p-6 bg-white/5 rounded-2xl border border-white/5 hover:border-indigo-500/30 transition-colors">
                    <h5 className="text-indigo-400 font-bold mb-3 uppercase tracking-widest text-xs">Rigor Técnico</h5>
                    <p className="text-sm text-slate-400">Escritura en tercera persona para mantener la objetividad y profesionalismo pedagógico.</p>
                  </div>
                  <div className="p-6 bg-white/5 rounded-2xl border border-white/5 hover:border-indigo-500/30 transition-colors">
                    <h5 className="text-indigo-400 font-bold mb-3 uppercase tracking-widest text-xs">Ejemplos Concretos</h5>
                    <p className="text-sm text-slate-400">Sugerencias prácticas (ej. usar palitos de helado para sumar) que conectan el aula con la vida diaria.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-40 relative">
          <div className="container mx-auto px-6 text-center reveal" ref={addToRevealRefs}>
            <div className="inline-flex gap-1 mb-12">
              {[1,2,3,4,5].map(i => <Award key={i} size={24} className="text-indigo-500 fill-indigo-500/20" />)}
            </div>
            <blockquote className="max-w-5xl mx-auto relative">
              <div className="absolute -top-20 -left-10 text-[200px] font-editorial text-indigo-600/10 pointer-events-none italic">"</div>
              <p className="text-4xl md:text-6xl font-editorial italic leading-tight mb-16 text-slate-200">
                La trazabilidad granular nos ha devuelto la capacidad de ver al estudiante como un proceso vivo, no como un resultado estático.
              </p>
              <footer className="flex flex-col items-center">
                <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full p-1 mb-8 shadow-2xl">
                  <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center text-white text-3xl font-bold font-editorial">C</div>
                </div>
                <div className="text-2xl font-bold text-white mb-2">Consejo de Dirección</div>
                <div className="text-indigo-400 font-black text-sm uppercase tracking-[0.3em]">Institución Partner Regional</div>
              </footer>
            </blockquote>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-32 relative overflow-hidden bg-gradient-to-b from-transparent to-indigo-950/20">
          <div className="container mx-auto px-6 relative z-10 text-center">
            <div className="reveal" ref={addToRevealRefs}>
              <h2 className="text-6xl md:text-9xl font-black mb-16 tracking-tighter text-white">
                El <span className="font-editorial italic font-normal text-indigo-500">Futuro</span> es hoy.
              </h2>
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                <button 
                  onClick={() => handleCTA('demo')}
                  className="w-full sm:w-auto px-16 py-6 bg-white text-[#020617] rounded-full font-black text-xl hover:bg-indigo-500 hover:text-white transition-all duration-500 shadow-2xl shadow-white/10"
                >
                  Agendar Demo
                </button>
                <button 
                  onClick={() => handleCTA('soporte')}
                  className="w-full sm:w-auto px-16 py-6 border-2 border-white/10 text-white rounded-full font-black text-xl hover:bg-white/5 transition-all duration-500"
                >
                  Contactar Soporte
                </button>
              </div>
            </div>
          </div>
          {/* Background decoration */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-indigo-600/10 blur-[150px] -z-10 rounded-full" />
        </section>
      </main>

      {/* Footer */}
      <footer className="py-24 border-t border-white/5 bg-slate-900/90 backdrop-blur-3xl">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row justify-between gap-20">
            <div className="max-w-md">
              <div className="flex items-center mb-10">
                <div className="h-20 flex items-center justify-center transition-all duration-500">
                <img 
                  src={`${process.env.PUBLIC_URL}/images/logos/logo-csn.png`} 
                  alt="Logo Colegio Sin Notas" 
                  className="h-full w-auto object-contain brightness-110" 
                />
                </div>
              </div>
              <p className="text-slate-400 text-lg leading-relaxed mb-10">
                Redefiniendo el éxito educativo a través de la trazabilidad profunda del desarrollo humano 
                y el cumplimiento de los Objetivos de Aprendizaje nacionales.
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-16 lg:gap-32">
              <div>
                <h4 className="text-white font-black text-xs uppercase tracking-[0.3em] mb-10">Plataforma</h4>
                <ul className="space-y-5 text-slate-500 font-bold text-sm">
                  <li><a href="#metodologia" className="hover:text-indigo-400 transition-colors">Metodología</a></li>
                  <li><a href="#proposito" className="hover:text-indigo-400 transition-colors">Propósito</a></li>
                  <li><a href="#colegios" className="hover:text-indigo-400 transition-colors">Impacto</a></li>
                  <li><a href="#dimensiones" className="hover:text-indigo-400 transition-colors">Estructura</a></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-black text-xs uppercase tracking-[0.3em] mb-10">Impacto</h4>
                <ul className="space-y-5 text-slate-500 font-bold text-sm">
                  <li><a href="#colegios" className="hover:text-indigo-400 transition-colors">Casos de Éxito</a></li>
                  <li><a href="#colegios" className="hover:text-indigo-400 transition-colors">SLEP Llanquihue</a></li>
                  <li><a href="#colegios" className="hover:text-indigo-400 transition-colors">Mundo Privado</a></li>
                </ul>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <h4 className="text-white font-black text-xs uppercase tracking-[0.3em] mb-10">Conectar</h4>
                <div className="space-y-6">
                  <div className="flex items-center gap-3 text-indigo-400 font-bold">
                    <Globe size={18} />
                    <span className="text-xs uppercase tracking-widest">Chile / Global</span>
                  </div>
                  <div className="text-slate-200 font-bold text-base hover:text-indigo-400 transition-colors cursor-pointer">
                    soporte@colegiosinnotas.cl
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-24 pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-black text-slate-600 uppercase tracking-[0.5em]">
            <div>© {new Date().getFullYear()} Colegio Sin Notas Digital. Excellence in Education.</div>
            <div className="flex gap-12">
              <a href="#privacy" className="hover:text-white transition-colors">Privacidad</a>
              <a href="#terms" className="hover:text-white transition-colors">Términos</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#020617]/80 backdrop-blur-sm" onClick={() => setShowContactModal(false)} />
          <div className="relative w-full max-w-xl glass rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden animate-slide-up">
            <button 
              onClick={() => setShowContactModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-white transition-colors z-10"
            >
              <X size={24} />
            </button>

            {formStatus === 'success' ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-emerald-500/20 text-emerald-500 rounded-3xl flex items-center justify-center mx-auto mb-8">
                  <CheckCircle2 size={40} />
                </div>
                <h3 className="text-3xl font-black text-white mb-4 tracking-tight">¡Solicitud Recibida!</h3>
                <p className="text-slate-400 text-lg leading-relaxed">
                  Gracias por tu interés. Un especialista de nuestro equipo se contactará contigo 
                  en las próximas 24 horas para coordinar los detalles.
                </p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="p-8 md:p-12">
                <div className="mb-10 text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-400 text-[10px] font-black uppercase tracking-widest mb-4">
                    {contactType === 'demo' ? 'Solicitud de Demostración' : 
                     contactType === 'soporte' ? 'Contacto Soporte Técnico' : 
                     'Plan de Evolución Educativa'}
                  </div>
                  <h3 className="text-3xl font-black text-white tracking-tight">Potenciemos tu Institución</h3>
                </div>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Nombre Completo</label>
                      <input 
                        required
                        type="text"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-600"
                        placeholder="Ej: Juan Pérez"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Correo Institucional</label>
                      <input 
                        required
                        type="email"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-600"
                        placeholder="juan@colegio.cl"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Institución Educativa</label>
                    <input 
                      required
                      type="text"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-600"
                      placeholder="Nombre del Colegio o Fundación"
                      value={formData.institution}
                      onChange={e => setFormData({...formData, institution: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Mensaje (Opcional)</label>
                    <textarea 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-600 min-h-[120px] resize-none"
                      placeholder="Cuéntanos un poco más sobre tus necesidades..."
                      value={formData.message}
                      onChange={e => setFormData({...formData, message: e.target.value})}
                    />
                  </div>

                  <button 
                    disabled={formStatus === 'loading'}
                    type="submit"
                    className="w-full bg-indigo-600 text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-2xl shadow-indigo-900/40 disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {formStatus === 'loading' ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      'Enviar Solicitud'
                    )}
                  </button>

                  {formStatus === 'error' && (
                    <p className="text-center text-rose-500 text-xs font-bold mt-4">
                      Ocurrió un error al enviar. Por favor intenta nuevamente.
                    </p>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
